// src/review/canvas/shared/EditableField.jsx
import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Stack, Text, Textarea, Group, Button, Badge, Tooltip } from '@mantine/core';
import { IconEdit, IconCheck, IconX } from '@tabler/icons-react';
import { useDispatch, useSelector } from 'react-redux';
import { trackEdit } from '../../../redux/slices/reviewSlice';
import { useReview } from '../../context/ReviewContext';

/**
 * EditableField - Reusable inline editable field component
 * Supports text and textarea with save/cancel actions
 */
function EditableField({
  label,
  value,
  itemType,
  itemId,
  fieldName,
  minRows,
  placeholder,
  readOnly,
  onSave,
}) {
  const dispatch = useDispatch();
  const { modelId, assessmentUuid } = useReview();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [saving, setSaving] = useState(false);

  // Get user info from app state
  const userInfo = useSelector((state) => {
    const user = state.app?.currentUser;
    return user ? {
      userId: user.sub || user.id || user.email,
      email: user.email,
      name: user.name || user.email?.split('@')[0],
    } : null;
  });

  // Get edit history for this field from RTK review state
  const reviewState = useSelector((state) =>
    state.review.byAssessment[assessmentUuid] || {}
  );
  const edits = reviewState.edits || {};

  // Find if this field has been edited
  const fieldEdit = Object.values(edits).flatMap(category => Object.values(category))
    .find(edit =>
      edit.itemId === itemId &&
      edit.fieldName === fieldName
    );

  const handleEdit = useCallback(() => {
    setEditValue(value || '');
    setIsEditing(true);
  }, [value]);

  const handleCancel = useCallback(() => {
    setEditValue(value || '');
    setIsEditing(false);
  }, [value]);

  // Sync internal editValue with value prop when it changes
  // This ensures UI updates after persist operations complete
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value || '');
    }
  }, [value, isEditing]);

  const handleSave = useCallback(async () => {
    // Skip if no change
    if (editValue === value) {
      setIsEditing(false);
      return;
    }

    setSaving(true);
    try {
      // 1. Call custom save handler if provided (for DB persistence)
      if (onSave) {
        await onSave(editValue, value);
      }

      // 2. Track edit for audit trail
      await dispatch(trackEdit({
        modelId,
        assessmentUuid,
        itemType,
        itemId,
        field: fieldName,
        oldValue: value,
        newValue: editValue,
        userInfo
      }));
      setIsEditing(false);
    } catch (error) {
      console.error('[EditableField] Error saving edit:', error);
    } finally {
      setSaving(false);
    }
  }, [editValue, value, onSave, dispatch, modelId, assessmentUuid, itemType, itemId, fieldName, userInfo]);

  if (readOnly) {
    return (
      <Stack gap="xs">
        <Text size="sm" fw={600} c="dimmed">{label}</Text>
        <Text size="sm">{value || placeholder || 'N/A'}</Text>
      </Stack>
    );
  }

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text size="sm" fw={600} c="dimmed">{label}</Text>
        {fieldEdit && !isEditing && (
          <Tooltip label={`Edited by ${fieldEdit.editorName || fieldEdit.editorEmail || 'Unknown'}`}>
            <Badge size="xs" variant="light" color="blue">
              Edited
            </Badge>
          </Tooltip>
        )}
      </Group>

      {isEditing ? (
        <Stack gap="xs">
          <Textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            minRows={minRows}
            placeholder={placeholder}
            autoFocus
          />
          <Group gap="xs">
            <Button
              size="xs"
              leftSection={<IconCheck size={14} />}
              onClick={handleSave}
              loading={saving}
            >
              Save
            </Button>
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconX size={14} />}
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
          </Group>
        </Stack>
      ) : (
        <Group
          gap="xs"
          style={{
            padding: '8px 12px',
            borderRadius: 4,
            border: '1px solid #e9ecef',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onClick={handleEdit}
        >
          <Text
            size="sm"
            style={{ flex: 1 }}
            c={value ? 'dark' : 'dimmed'}
          >
            {value || placeholder || 'Click to edit...'}
          </Text>
          <IconEdit size={16} color="#868e96" />
        </Group>
      )}
    </Stack>
  );
}

EditableField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  itemType: PropTypes.string.isRequired,
  itemId: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  minRows: PropTypes.number,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  onSave: PropTypes.func,
};

EditableField.defaultProps = {
  value: '',
  minRows: 3,
  placeholder: '',
  readOnly: false,
  onSave: null,
};

export default EditableField;
